/**
 * shared/pmove/sprint.ts — PM_UpdateSprint (0x9452f0) and its helpers [PM §3 "Sprint state machine"].
 *
 * OWNER: PMOVE agent.
 *
 *  - max length = ps.sprintDurationBase (server: setsprintduration(4) -> 4000 ms) x weapon
 *    sprintDurationScale (x perk_sprintMultiplier 2.0 with Extreme Conditioning: never in SnS),
 *    clamped to 0x3fff ms [PM §3 PM_SprintMaxDuration 0x414220].
 *  - the meter refills 1 ms per ms after a sprint ends [PM §3 PM_SprintTimeRemaining 0x4cfb70].
 *  - a sprint can only start with > 1.0 s left (0x6b56a0); running out sets sprintDelay (then the
 *    server cooldown, 0 in SnS, applies); ending a sprint with jump pushes lastSprintEnd +800 ms.
 *  - ends on ADS / fire / jump / melee / offhand / stance buttons / low forward / back
 *    (forwardmove <= player_sprintForwardMinimum, ESTIMATE 105) / lean / weapon states
 *    [PM §3 PM_SprintEndCheck 0x943e20; T5 PM_SprintEndingButtons].
 * Button bit mapping T6/T5 -> ours is ESTIMATE where the T6 bit numbers are unmapped (PM §1).
 */
import { PM_SPRINT_JUMP_END_DELAY_MS, PM_SPRINT_MIN_START_MS } from '../constants';
import { Button, EF, PEv, PMF, PmType, type PlayerState } from '../types';
import { canCrouch, canStand } from './stance';
import type { Pm } from './pm';

/** Buttons that end a sprint [T5 PM_SprintEndingButtons: 10 jump, 4, 2, 0 attack, 5, 14/15 lean, 8/9 stance, 24 offhand; T6 adds ADS]. */
const SPRINT_ENDING_BUTTONS = Button.JUMP | Button.ATTACK | Button.ADS | Button.MELEE | Button.FRAG | Button.TACTICAL
  | Button.CROUCH | Button.PRONE | Button.LEAN_LEFT | Button.LEAN_RIGHT | Button.RELOAD;
/** Buttons that stop a sprint from starting [T5 PM_SprintStartInterferingButtons]: no stance buttons, no ADS button (the sight-aim flag covers ADS). */
const SPRINT_START_BLOCK_BUTTONS = Button.JUMP | Button.ATTACK | Button.MELEE | Button.FRAG | Button.TACTICAL
  | Button.LEAN_LEFT | Button.LEAN_RIGHT | Button.RELOAD;

/** PM_SprintMaxDuration (0x414220). */
export function sprintMaxDuration(pm: Pm): number {
  const ps = pm.ps;
  // WeaponId.NONE has an all-zero def here; the engine's default weapon has scale 1 (ESTIMATE)
  const wscale = pm.ctx.weaponDef(ps.weapon).sprintDurationScale || 1;
  const base = ps.eFlags & EF.NO_SPRINT_LIMIT ? 740 : ps.sprintDurationBase * wscale;
  return Math.min(Math.trunc(base), 0x3fff);
}

/** PM_IsSprinting [T5]. */
export function isSprinting(ps: PlayerState): boolean {
  return (ps.pm_flags & PMF.SPRINTING) !== 0;
}

/**
 * PM_SprintTimeRemaining (0x4cfb70) / T5 PM_GetSprintLeft: what is left of the meter at `now`.
 * Exposed for the HUD (sprint meter is not drawn in BO2, but bots use it).
 */
export function sprintTimeRemaining(pm: Pm, now: number): number {
  const ps = pm.ps;
  const maxLen = sprintMaxDuration(pm);
  const S = ps.lastSprintStart, E = ps.lastSprintEnd, L = ps.sprintStartMaxLength;
  let remaining: number;
  if (!S) remaining = maxLen; // never sprinted this life [T5 PM_GetSprintLeft]
  else if (E < S) remaining = L - (now - S); // still sprinting
  else if (!ps.sprintDelay) remaining = L - (E - S) + (now - E);
  else remaining = L - (E - S) + (now - E) - ps.sprintCooldown;
  if (remaining < 0) remaining = 0;
  return remaining > maxLen ? maxLen : remaining;
}

function sprintEndCheck(pm: Pm): boolean {
  const ps = pm.ps;
  if (ps.waterlevel >= 2) return true;
  if (ps.pm_flags & (PMF.LADDER | PMF.UNK_0x10 | PMF.VEHICLE_TURRET)) return true; // 0x10 = sight aiming
  if (pm.cmd.forwardmove <= pm.ctx.dvars.sprintForwardMinimum) return true;
  if (pm.cmd.buttons & SPRINT_ENDING_BUTTONS) return true;
  if (ps.leanf !== 0) return true;
  if (pm.ctx.weapons.blocksSprint(ps)) return true;
  return (ps.pm_flags & (PMF.UNK_0x400000 | PMF.NO_SPRINT)) !== 0;
}

function sprintStartInterfering(pm: Pm): boolean {
  const ps = pm.ps;
  if (ps.pm_flags & PMF.LADDER) return true;
  if (ps.waterlevel >= 2) return true;
  if (pm.cmd.forwardmove <= pm.ctx.dvars.sprintForwardMinimum) return true;
  if (pm.cmd.buttons & SPRINT_START_BLOCK_BUTTONS) return true;
  if (ps.leanf !== 0) return true;
  if (ps.pm_flags & (PMF.VEHICLE_TURRET | PMF.UNK_0x10 | PMF.LADDER | PMF.MANTLE)) return true;
  if ((ps.pm_flags & PMF.JUMPING) && !ps.pm_time) return false; // airborne in a jump: may re-engage
  if (pm.ctx.weapons.blocksSprint(ps)) return true;
  return (ps.pm_flags & (PMF.UNK_0x400000 | PMF.DIVE)) !== 0;
}

/** PM_EndSprint [T5 bg_pmove.cpp:2276]. */
export function endSprint(pm: Pm): void {
  const ps = pm.ps;
  if (!(ps.pm_flags & PMF.SPRINTING)) return;
  ps.sprintDelay = 0;
  ps.lastSprintEnd = pm.cmd.serverTime;
  ps.pm_flags &= ~PMF.SPRINTING;
  ps.eFlags &= ~EF.SPRINTING;
  if (pm.cmd.buttons & Button.SPRINT) ps.sprintButtonUpRequired = 1;
  pm.ctx.addEvent(ps, PEv.SPRINT_END, 0);
}

function startSprint(pm: Pm, sprintLeft: number): void {
  const ps = pm.ps;
  ps.sprintStartMaxLength = sprintLeft;
  ps.lastSprintStart = pm.cmd.serverTime;
  ps.pm_flags |= PMF.SPRINTING;
  ps.eFlags |= EF.SPRINTING;
  pm.ctx.addEvent(ps, PEv.SPRINT_START, 0);
}

/** PM_UpdateSprint (0x9452f0). */
export function updateSprint(pm: Pm): void {
  const ps = pm.ps;
  const now = pm.cmd.serverTime;
  if (ps.sprintButtonUpRequired && !(pm.cmd.buttons & Button.SPRINT) && !(ps.pm_flags & PMF.UNK_0x800)) {
    ps.sprintButtonUpRequired = 0;
  }
  if ((ps.pm_type !== PmType.NORMAL && ps.pm_type !== PmType.NORMAL_LINKED) || sprintMaxDuration(pm) <= 0) {
    endSprint(pm);
    return;
  }
  if (ps.pm_flags & PMF.SPRINTING) {
    if (now - ps.lastSprintStart >= ps.sprintStartMaxLength) {
      endSprint(pm); // ran out
      ps.sprintDelay = 1;
      return;
    }
    if (sprintEndCheck(pm)) {
      endSprint(pm);
      if (pm.cmd.buttons & Button.JUMP) ps.lastSprintEnd += PM_SPRINT_JUMP_END_DELAY_MS; // 0x320
    }
    return;
  }
  if (ps.sprintDelay && now - ps.lastSprintEnd < ps.sprintCooldown) return;
  if (!(pm.cmd.buttons & Button.SPRINT)) return;
  if (ps.pm_flags & PMF.NO_SPRINT) return;
  if (ps.sprintButtonUpRequired) return;
  if (sprintStartInterfering(pm)) return;
  if (!canCrouch(pm) || !canStand(pm)) return;
  const sprintLeft = sprintTimeRemaining(pm, now);
  if (sprintLeft > PM_SPRINT_MIN_START_MS) startSprint(pm, sprintLeft); // > 1.0 s (0x6b56a0)
}
